import { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Typography from 'components/atoms/Typography';
import TimeConfig from 'components/molecules/settings/TimeConfigs';
import Button from 'components/atoms/Button';
import SelectV2 from 'components/atoms/Select';
import ConfigExceptions from 'components/molecules/Modal/contents/ConfigExceptions';
import {
  Divider,
  InputDescription,
  InputTitle,
} from 'components/atoms/styleds';
import { nanoid } from 'nanoid';
import Modal from 'components/molecules/Modal';
import { deliveryKeys } from 'common/constants/settings';
import UIActionsHelper from 'services/UIActionsHelper';
import { Delete } from '../../v2/ServicesList/styled';
import { ReactComponent as DeleteIcon } from 'assets/images/delete.svg';
import { StyledFlexbox } from 'components/atoms/Flexbox/styled';
import {
  ConfigBlock,
  TimeItemsWrapper,
  ExceptionsWrapper,
  ExceptionRow,
} from '../../styled';
import moment from 'moment';
import BaseErrors from 'components/atoms/BaseError';

const STORAGE_KEY = 'opening_times_checkboxes_state';

const getCheckboxStates = (restaurantId, variant) => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const allStates = JSON.parse(saved);
      return allStates[restaurantId]?.[variant] || {};
    }
  } catch (error) {
    console.error('Error reading from localStorage:', error);
  }
  return {};
};

const saveCheckboxStates = (restaurantId, variant, dayId, isChecked) => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    const allStates = saved ? JSON.parse(saved) : {};

    if (!allStates[restaurantId]) {
      allStates[restaurantId] = {};
    }

    if (!allStates[restaurantId][variant]) {
      allStates[restaurantId][variant] = {};
    }

    allStates[restaurantId][variant][dayId] = isChecked;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allStates));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

const ExceptionItem = ({
  data,
  inProcess,
  onDelete,
  onClickEdit,
  onChangeDay,
  exceptionErrors,
}) => {
  const currentError =
    exceptionErrors &&
    exceptionErrors[moment(data.date_from).format('YYYY-MM-DD')];
  const currentErrorTo =
    exceptionErrors &&
    exceptionErrors[moment(data.date_to).format('YYYY-MM-DD')];

  return (
    <>
      <ExceptionRow>
        <div>
          <InputDescription mb={0}>
            <span style={{ textTransform: 'capitalize' }}>
              {moment(data.date_from).format('dddd D MMMM')}{' '}
              {moment(data.date_to).format('dddd D MMMM')}
            </span>
            : {data.day_off ? 'Chiuso' : `${data.from} ${data.to}`}
          </InputDescription>
        </div>
        <StyledFlexbox align='center'>
          <Delete
            as={DeleteIcon}
            disabled={inProcess}
            role='presentation'
            onClick={() => onDelete(data)}
          />
        </StyledFlexbox>
      </ExceptionRow>
      <BaseErrors errors={currentError} errorKey='date_from' />
      <BaseErrors errors={currentErrorTo} errorKey='date_to' />
      <BaseErrors errors={currentError} errorKey='start_date' />
      <BaseErrors errors={currentError} errorKey='to`' />
      <Divider />
    </>
  );
};

const getBlockInfo = (variant) => {
  switch (variant) {
    case deliveryKeys.reservation:
      return {
        title: 'Prenotazione tavoli',
      };
    case deliveryKeys.takeaway:
      return {
        title: 'Asporto',
      };
    case deliveryKeys.delivery:
      return {
        title: 'Delivery',
        showMaximumDelivery: true,
      };
    default:
      return {
        title: 'Prenotazione tavoli',
      };
  }
};

const items = {
  first: [
    { id: 0, title: 'Lunedì' },
    { id: 1, title: 'Martedì' },
    { id: 2, title: 'Mercoledì' },
  ],
  second: [
    { id: 3, title: 'Giovedì' },
    { id: 4, title: 'Venerdì' },
    { id: 5, title: 'Sabato' },
  ],
  third: [{ id: 6, title: 'Domenica' }],
};

const OpeningTimes = ({
  data,
  variant,
  onChangeMaximum,
  onSelectTime,
  onSelectDay,
  onDeleteTimeItem,
  onClickCreate,
  exceptionProps,
  openingHourErrors,
  onAddNewException,
  exceptionErrors,
  restaurantId: restaurantIdProp,
}) => {
  const { title, showMaximumDelivery } = getBlockInfo(variant);
  const [showModal, setShowModal] = useState(false);
  const [selectedExceptionId, setSelectedExceptionId] = useState(null);
  const [newExceptionData, setExceptionNewData] = useState(null);
  const [checkboxStates, setCheckboxStates] = useState({});
  const [restaurantId, setRestaurantId] = useState(null);

  useEffect(() => {
    let id = restaurantIdProp;

    if (!id) {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          const user = JSON.parse(userData);
          id = user.restaurant_id;
        }

        if (!id) {
          const restaurantData = localStorage.getItem('restaurant');
          if (restaurantData) {
            const restaurant = JSON.parse(restaurantData);
            id = restaurant.id || restaurant.restaurant_id;
          }
        }

        if (!id) {
          id = localStorage.getItem('restaurant_id');
        }
      } catch (error) {
        console.error(error);
      }
    }

    setRestaurantId(id);
  }, [restaurantIdProp]);

  useEffect(() => {
    if (restaurantId) {
      const savedStates = getCheckboxStates(restaurantId, variant);
      setCheckboxStates(savedStates);
    }
  }, [variant, restaurantId]);

  const handleSelectDay = useCallback(
    (selectedData) => {
      if (!restaurantId) {
        console.warn('no restaurantId');
        if (onSelectDay) onSelectDay(selectedData);
        return;
      }

      const dayId = selectedData?.id;
      if (dayId === undefined) {
        console.error('no dayId');
        if (onSelectDay) onSelectDay(selectedData);
        return;
      }

      const currentState = checkboxStates[dayId] || false;
      const newState = !currentState;

      setCheckboxStates((prev) => ({
        ...prev,
        [dayId]: newState,
      }));

      saveCheckboxStates(restaurantId, variant, dayId, newState);

      if (onSelectDay) onSelectDay(selectedData);
    },
    [variant, restaurantId, onSelectDay, checkboxStates],
  );

  const onClickShowModal = useCallback(() => {
    const id = nanoid();
    const now = new Date();
    setSelectedExceptionId(id);
    setExceptionNewData({
      id,
      date_from: now,
      date_to: new Date(new Date().setDate(now.getDate() + 1)),
      from: '10:00',
      to: '13:00',
      break_from: null,
      break_to: null,
      day_off: false,
    });
  }, []);

  const onSelectException = useCallback((i) => {
    setSelectedExceptionId(i);
  }, []);

  const onSubmitModal = useCallback(() => {
    setSelectedExceptionId(null);
    if (newExceptionData) {
      onAddNewException(newExceptionData);
    }
  }, [newExceptionData, onAddNewException]);

  const handleChangeSelectTime = useCallback((e) => {
    const {
      target: { name, value },
    } = e;
    setExceptionNewData((s) => ({ ...s, [name]: value }));
  }, []);

  const handleSelect = useCallback((i) => {
    const { id, selectedDay } = i;
    const [key, variant] = id.split('-');
    setExceptionNewData({
      ...data,
      day_off: variant === 'on',
      from: '10:00',
      to: '20:00',
      break_from: null,
      break_to: null,
    });
  }, []);

  const onClickDelete = useCallback((item) => {
    if (item.id === 'pause') {
      setExceptionNewData((s) => ({
        ...s,
        break_from: null,
        break_to: null,
      }));
    }
  }, []);

  const onChangeDay = useCallback((date, key) => {
    setExceptionNewData((s) => ({ ...s, [key]: date }));
  }, []);

  const onClickCreatePause = useCallback(() => {
    setExceptionNewData((s) => ({
      ...s,
      break_from: '15:00',
      break_to: '16:00',
    }));
  }, []);

  const renderTimeItems = (itemsList) => {
    return itemsList.map((i) => {
      const itemData = UIActionsHelper.getDeliveryHoursItem(
        data?.opening_hours,
        i.id,
      );
      const savedState = checkboxStates[i.id];

      let mergedItemData = { ...itemData };
      if (savedState !== undefined) {
        mergedItemData.day_off = !savedState;
      }

      return (
        <ConfigBlock key={i.id} showBorder={false}>
          <TimeConfig
            onSelectTime={onSelectTime}
            onClickDelete={onDeleteTimeItem}
            onClickCreate={onClickCreate}
            onSelect={handleSelectDay}
            item={{ ...i }}
            itemData={mergedItemData}
            errors={openingHourErrors && openingHourErrors[i.id]}
          />
        </ConfigBlock>
      );
    });
  };

  return (
    <>
      <Typography variant='title'>{title}</Typography>
      <div>
        <TimeItemsWrapper>{renderTimeItems(items.first)}</TimeItemsWrapper>
        <TimeItemsWrapper>{renderTimeItems(items.second)}</TimeItemsWrapper>
        <TimeItemsWrapper>{renderTimeItems(items.third)}</TimeItemsWrapper>
      </div>
      <Divider style={{ width: '100%' }} />
      <ExceptionsWrapper p='20px 20px 40px 20px'>
        <InputTitle>Eccezioni</InputTitle>
        <InputDescription>
          Inserisci eventuali variazioni rispetto ai regolari <br /> orari del
          tuo servizio. <br />
          Es: aperture domenicali, chiusure per ferie, feste <br /> nazionali.
        </InputDescription>
        <Button color='primary_light' p='0 15px' onClick={onClickShowModal}>
          Aggiungi eccezione
        </Button>
      </ExceptionsWrapper>
      <Divider />
      <ExceptionsWrapper p='20px 20px 30px 20px'>
        <InputTitle mb={0}>Riepilogo eccezioni</InputTitle>
        {data?.opening_hour_exceptions?.map((exc, index) => (
          <ExceptionItem
            key={`${exc.date}_${index}`}
            data={exc}
            onDelete={exceptionProps.onDelete}
            onClickEdit={() => onSelectException(exc.id)}
            onChangeDay={exceptionProps.onChangeDay}
            exceptionErrors={exceptionErrors}
          />
        ))}
        {data?.opening_hour_exceptions?.length === 0 && <Divider mt={20} />}
      </ExceptionsWrapper>
      {showMaximumDelivery && (
        <>
          <ExceptionsWrapper p='0 20px 0 20px' isLast={true}>
            <InputTitle>Numero massimo di ordini per Delivery</InputTitle>
            <InputDescription>
              Imposta il numero massimo di ordini delivery evadibili ogni 30
              minuti.
            </InputDescription>
            <ConfigBlock showBorder={false} style={{ padding: 0 }}>
              <SelectV2
                label='Numero massimo di ordini'
                standardWidth={true}
                onChange={onChangeMaximum}
                items={[
                  { id: 1, title: 1 },
                  { id: 2, title: 2 },
                  { id: 3, title: 3 },
                  { id: 4, title: 4 },
                  { id: 5, title: 5 },
                  { id: 6, title: 6 },
                  { id: 7, title: 7 },
                  { id: 8, title: 8 },
                  { id: 9, title: 9 },
                  { id: 10, title: 10 },
                ]}
                errors={
                  openingHourErrors && openingHourErrors?.max_delivery_orders
                }
                selectedKey={data?.settings?.max_delivery_orders}
                name='max_delivery_orders'
              />
            </ConfigBlock>
          </ExceptionsWrapper>
          <Divider mb={30} mt={30} />
        </>
      )}
      <Modal
        variant='config'
        title='Crea eccezioni'
        onClose={() => onSelectException(null)}
        onCancel={() => onSelectException(null)}
        onSubmit={onSubmitModal}
        open={!!selectedExceptionId}
        desktopMinH='40vh'
        actionButtonText='Aggiungi eccezione'
        cancelButtonText='Indietro'
        body={
          <ConfigExceptions
            selectedExceptionId={selectedExceptionId}
            newExceptionData={newExceptionData}
            {...exceptionProps}
            onSelectTime={handleChangeSelectTime}
            onSelect={handleSelect}
            onClickDelete={onClickDelete}
            onChangeDay={onChangeDay}
            onClickCreate={onClickCreatePause}
          />
        }
      />
    </>
  );
};

OpeningTimes.propTypes = {
  variant: PropTypes.oneOf(Object.keys(deliveryKeys)),
  restaurantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default OpeningTimes;
